import { defineStore } from "pinia";

const defaults = {
  searchEndpoint: null,
  loaded: false,
  q: "",
  sortBy: "updated_at",
  sortDirection: "desc",
  archived: null,
  userId: null,
  visibleLabels: [],
  totalEntries: 0,
  currentPage: 1,
  perPage: 20,
  archivedLabelsCount: 0,
};

const queryParamMap = {
  q: "q",
  perPage: "per_page",
  currentPage: "page",
  sortBy: "sort_by",
  sortDirection: "sort_direction",
  archived: "archived",
  userId: "user_id",
};

const validSorts = ["name", "updated_at"];

export const useSupplementSearchStore = defineStore("supplementSearch", {
  persist: {
    storage: sessionStorage,
    paths: ["q", "sortBy", "sortDirection"],
  },

  state: () => ({ ...defaults }),

  getters: {
    hasQuery: (state) => state.q !== "",

    requestQueryParams: (state) => {
      const p = new URLSearchParams();

      if (state.q !== "") {
        p.append(queryParamMap["q"], state.q);
      }

      if (state.archived !== null) {
        p.append(queryParamMap["archived"], state.archived);
      }

      if (state.userId) {
        p.append(queryParamMap["userId"], state.userId);
      }

      p.append(queryParamMap["perPage"], state.perPage);
      p.append(queryParamMap["currentPage"], state.currentPage);
      p.append(queryParamMap["sortBy"], state.sortBy);
      p.append(queryParamMap["sortDirection"], state.sortDirection);

      return p;
    },

    requestQueryString() {
      return this.requestQueryParams.toString();
    },
  },

  actions: {
    setSearchEndpoint(url) {
      this.searchEndpoint = url;
    },

    loadInitial({ force = false } = {}) {
      if (force) {
        this.loaded = false;
      }
      if (!this.loaded) {
        this.loadPage();
      }
    },

    async loadPage(newPage) {
      if (newPage !== undefined) {
        this.currentPage = newPage;
      }

      const response = await fetch(`${this.searchEndpoint}?${this.requestQueryString}`, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
      });

      const json = await response.json();

      this.visibleLabels = json.labels;
      this.totalEntries = json.total_entries;
      this.perPage = json.per_page;
      this.currentPage = json.current_page;
      this.archivedLabelsCount = json.archived_labels_count || 0;
      this.loaded = true;
    },

    setQueryFilter(q) {
      this.q = q;
      this.loadPage(1);
    },

    setSort(sortBy, sortDirection) {
      if (!validSorts.includes(sortBy)) {
        sortBy = "updated_at";
      }
      this.sortBy = sortBy;
      if (sortDirection !== undefined) {
        this.sortDirection = sortDirection;
      }

      this.loadPage(1);
    },

    toggleSort(field) {
      if (this.sortBy === field) {
        this.setSort(field, this.sortDirection === "desc" ? "asc" : "desc");
      } else {
        this.setSort(field, "asc");
      }
    },

    setArchivedFilter(archived) {
      this.archived = archived;
      this.loadPage(1);
    },

    resetSearch() {
      const { searchEndpoint, userId } = this;
      this.$patch({
        ...defaults,
        searchEndpoint,
        userId,
      });
      this.loadPage(1);
    },
  },
});
